import { Input } from './Input'
import { DatePicker } from './DatePicker'
import { Select } from './Select'
import { MultiSelect } from './MultiSelect'
import s from './FilterBuilder.module.css'

/**
 * FilterBuilder — the multi-field condition builder. Emits `Filter[]` (the core
 * rules `Leaf` shape: {field, op, value}), the SAME payload the grid views and the
 * MCP filter tools already speak — one filter grammar, everywhere. Used for the
 * Lookup's advanced search and (later) the list-view column filters.
 */
export interface FilterFieldDef {
  name: string
  label: string
  type?: 'text' | 'number' | 'date' | 'select'
  options?: { value: string; label: string }[]
}
export interface Filter { field: string; op: string; value: unknown }

const OPS: Record<string, [string, string][]> = {
  text: [['contains', 'contains'], ['eq', 'is'], ['ne', 'is not'], ['empty', 'is empty']],
  number: [['eq', '='], ['ne', '≠'], ['gt', '>'], ['gte', '≥'], ['lt', '<'], ['lte', '≤']],
  date: [['eq', 'on'], ['after', 'after'], ['before', 'before'], ['gte', 'on/after'], ['lte', 'on/before']],
  select: [['in', 'is any of'], ['eq', 'is'], ['ne', 'is not']],
}
const opsFor = (t?: string) => OPS[t ?? 'text'] ?? OPS.text!
/** `in` ("is any of") carries a LIST; every other op a scalar. A row entering
 *  `in` with `''` crashed the builder: MultiSelect maps its value, and `'' ?? []`
 *  is still `''` (#2881). So the value's shape follows the op, on every path in. */
const valueFor = (op: string, prev: unknown = ''): unknown => (op === 'in'
  ? (Array.isArray(prev) ? prev : prev === '' || prev == null ? [] : [String(prev)])
  : (Array.isArray(prev) ? (prev[0] ?? '') : prev))

export function FilterBuilder({ fields, value, onChange, testid }: {
  fields: FilterFieldDef[]
  value: Filter[]
  onChange: (filters: Filter[]) => void
  testid?: string
}) {
  const defOf = (name: string) => fields.find((f) => f.name === name)
  const set = (i: number, patch: Partial<Filter>) => onChange(value.map((f, j) => (j === i ? { ...f, ...patch } : f)))
  const add = () => { const f = fields[0]; if (f) { const op = opsFor(f.type)[0]![0]; onChange([...value, { field: f.name, op, value: valueFor(op) }]) } }
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i))

  return (
    <div className={s.wrap} data-testid={testid}>
      {value.map((cond, i) => {
        const def = defOf(cond.field)
        const type = def?.type ?? 'text'
        return (
          <div key={i} className={s.row} data-testid={testid ? `${testid}-row` : undefined}>
            <Select value={cond.field} options={fields.map((f) => ({ value: f.name, label: f.label }))}
              onValueChange={(name) => { const d = defOf(name); const op = opsFor(d?.type)[0]![0]; set(i, { field: name, op, value: valueFor(op) }) }} />
            <Select value={cond.op} options={opsFor(type).map(([v, l]) => ({ value: v, label: l }))}
              onValueChange={(op) => set(i, { op, value: valueFor(op, cond.value) })} />
            {cond.op === 'empty' ? <span className={s.noVal}>—</span>
              : type === 'select' && def?.options
                ? (cond.op === 'in'
                    ? <MultiSelect value={Array.isArray(cond.value) ? (cond.value as string[]) : []} options={def.options} onChange={(v) => set(i, { value: v })} />
                    : <Select value={String(cond.value ?? '')} options={def.options} onValueChange={(v) => set(i, { value: v })} placeholder="Value…" />)
                : type === 'date' ? <DatePicker value={String(cond.value ?? '')} onChange={(next) => set(i, { value: next })} />
                  : <Input type={type === 'number' ? 'number' : 'text'} value={String(cond.value ?? '')}
                    placeholder="Value…" onChange={(e) => set(i, { value: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })} />}
            <button type="button" className={s.rm} aria-label="Remove condition" onClick={() => remove(i)}>×</button>
          </div>
        )
      })}
      <button type="button" className={s.add} onClick={add} data-testid={testid ? `${testid}-add` : undefined}>＋ condition</button>
    </div>
  )
}
